import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Loader2, 
  Phone, 
  Mail, 
  User,
  Gift,
  PartyPopper,
  Frown,
  RotateCw,
  ArrowLeft,
  Maximize2,
  Minimize2,
  RefreshCw,
  Coins,
  Ticket,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";
import api, { publicGamesAPI } from "../lib/api";

// Fortune Wheel Component
const FortuneWheel = ({ prizes, primaryColor, onSpinEnd, spinning, setSpinning }) => {
  const canvasRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  
  const segments = prizes.length > 0 ? prizes : [{ name: "Lot 1" }, { name: "Lot 2" }, { name: "Lot 3" }];
  const colors = [
    primaryColor,
    "#3B82F6",
    "#10B981", 
    "#8B5CF6",
    "#EC4899",
    "#F59E0B",
    "#06B6D4",
    "#EF4444"
  ];

  useEffect(() => {
    drawWheel();
  }, [segments, rotation]);

  const drawWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const arc = (2 * Math.PI) / segments.length;
    
    segments.forEach((segment, i) => {
      const angle = i * arc + (rotation * Math.PI / 180);
      
      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, angle, angle + arc);
      ctx.closePath();
      ctx.fillStyle = colors[i % colors.length];
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw text
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angle + arc / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px Outfit, sans-serif";
      ctx.shadowColor = "rgba(0,0,0,0.3)";
      ctx.shadowBlur = 3;
      
      const text = segment.name.length > 12 ? segment.name.substring(0, 12) + "..." : segment.name;
      ctx.fillText(text, radius - 20, 5);
      ctx.restore();
    });
    
    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30, 0, 2 * Math.PI);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = 4;
    ctx.stroke();
  };

  const spin = useCallback((winningIndex) => {
    if (spinning) return;
    setSpinning(true);
    
    const arc = 360 / segments.length;
    const targetAngle = 360 - (winningIndex * arc) - (arc / 2);
    const spins = 5 + Math.random() * 3;
    const finalRotation = spins * 360 + targetAngle + (Math.random() * 20 - 10);
    
    let start = null;
    const duration = 5000;
    const startRotation = rotation;
    
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const progress = (timestamp - start) / duration;
      
      if (progress < 1) {
        const easeOut = 1 - Math.pow(1 - progress, 4);
        setRotation(startRotation + finalRotation * easeOut);
        requestAnimationFrame(animate);
      } else {
        setRotation(startRotation + finalRotation);
        setSpinning(false);
        onSpinEnd();
      }
    };
    
    requestAnimationFrame(animate);
  }, [spinning, rotation, segments.length, onSpinEnd, setSpinning]);

  return { canvasRef, spin };
};

// Scratch Card Component - Full Screen Portrait Mode
const ScratchCard = ({ 
  onReveal, 
  revealed, 
  result, 
  primaryColor, 
  scratchLogo, 
  scratchImage,
  isFullScreen,
  loseMessages = ["Pas de chance !", "Dommage.. c'était pas loin !", "Retentez votre chance !"],
  loseImage = "",
  loseVideoUrl = ""
}) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercent, setScratchPercent] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loseMessageIndex] = useState(Math.floor(Math.random() * loseMessages.length));
  
  // Dimensions based on fullscreen mode
  const cardWidth = isFullScreen ? 320 : 288;
  const cardHeight = isFullScreen ? 480 : 192;
  
  // Get the message to display
  const loseMessage = loseMessages[loseMessageIndex] || "Pas de chance !";
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    
    // Draw scratch layer
    const drawScratchLayer = () => {
      // Background gradient or image
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, primaryColor);
      gradient.addColorStop(1, "#6366F1");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Add sparkle pattern
      ctx.fillStyle = "rgba(255,255,255,0.15)";
      for (let i = 0; i < 30; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 20 + 5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Add logo if provided
      if (scratchLogo) {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.onload = () => {
          const logoHeight = isFullScreen ? height * 0.2 : height * 0.3;
          const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          const logoX = (width - logoWidth) / 2;
          const logoY = isFullScreen ? height * 0.15 : height * 0.15;
          ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
          
          // Add text below logo
          ctx.font = `bold ${isFullScreen ? 28 : 20}px Outfit, Arial, sans-serif`;
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.textAlign = "center";
          ctx.fillText("GRATTEZ ICI", width / 2, isFullScreen ? height * 0.45 : height * 0.65);
          ctx.font = `${isFullScreen ? 16 : 14}px Outfit, Arial, sans-serif`;
          ctx.fillStyle = "rgba(255,255,255,0.6)";
          ctx.fillText("pour découvrir votre gain", width / 2, isFullScreen ? height * 0.52 : height * 0.75);
        };
        const logoUrl = scratchLogo.startsWith('/uploads') 
          ? `${process.env.REACT_APP_BACKEND_URL}/api${scratchLogo}`
          : scratchLogo;
        logoImg.src = logoUrl;
      } else {
        // No logo - just text
        ctx.font = `bold ${isFullScreen ? 32 : 24}px Outfit, Arial, sans-serif`;
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.textAlign = "center";
        ctx.fillText("GRATTEZ ICI", width / 2, height / 2 - 10);
        ctx.font = `${isFullScreen ? 18 : 14}px Outfit, Arial, sans-serif`;
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.fillText("pour découvrir votre gain", width / 2, height / 2 + 25);
      }
    };
    
    // If there's a scratch card image, load it first
    if (scratchImage) {
      const bgImg = new Image();
      bgImg.crossOrigin = "anonymous";
      bgImg.onload = () => {
        ctx.drawImage(bgImg, 0, 0, width, height);
        // Add overlay for better text visibility
        ctx.fillStyle = "rgba(0,0,0,0.3)";
        ctx.fillRect(0, 0, width, height);
        
        // Add logo if provided
        if (scratchLogo) {
          const logoImg = new Image();
          logoImg.crossOrigin = "anonymous";
          logoImg.onload = () => {
            const logoHeight = isFullScreen ? height * 0.2 : height * 0.3;
            const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
            const logoX = (width - logoWidth) / 2;
            const logoY = isFullScreen ? height * 0.15 : height * 0.15;
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
            
            ctx.font = `bold ${isFullScreen ? 28 : 20}px Outfit, Arial, sans-serif`;
            ctx.fillStyle = "rgba(255,255,255,0.95)";
            ctx.textAlign = "center";
            ctx.fillText("GRATTEZ ICI", width / 2, isFullScreen ? height * 0.45 : height * 0.65);
          };
          const logoUrl = scratchLogo.startsWith('/uploads') 
            ? `${process.env.REACT_APP_BACKEND_URL}/api${scratchLogo}`
            : scratchLogo;
          logoImg.src = logoUrl;
        } else {
          ctx.font = `bold ${isFullScreen ? 32 : 24}px Outfit, Arial, sans-serif`;
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.textAlign = "center";
          ctx.fillText("GRATTEZ ICI", width / 2, height / 2);
        }
        setImageLoaded(true);
      };
      bgImg.onerror = () => {
        // Fallback to gradient if image fails
        drawScratchLayer();
        setImageLoaded(true);
      };
      const imageUrl = scratchImage.startsWith('/uploads') 
        ? `${process.env.REACT_APP_BACKEND_URL}/api${scratchImage}`
        : scratchImage;
      bgImg.src = imageUrl;
    } else {
      drawScratchLayer();
      setImageLoaded(true);
    }
  }, [primaryColor, revealed, scratchLogo, scratchImage, isFullScreen]);

  const handleScratch = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, isFullScreen ? 35 : 25, 0, Math.PI * 2);
    ctx.fill();
    
    // Calculate scratch percentage
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) transparent++;
    }
    const percent = (transparent / (imageData.data.length / 4)) * 100;
    setScratchPercent(percent);
    
    if (percent > 50 && !revealed) {
      onReveal();
    }
  };

  // Helper to get proper image URL
  const getResultImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith('/uploads') 
      ? `${process.env.REACT_APP_BACKEND_URL}/api${url}`
      : url;
  };

  // Get background image/video for result
  const winResultImage = result?.prize?.result_image;
  const winResultVideo = result?.prize?.result_video_url;

  return (
    <div 
      className={`relative mx-auto rounded-2xl overflow-hidden shadow-2xl ${
        isFullScreen ? "w-80 h-[480px]" : "w-72 h-48"
      }`}
    >
      {/* Result layer with optional background image/video */}
      <div 
        className={`absolute inset-0 flex flex-col items-center justify-center ${
          result?.won 
            ? "bg-gradient-to-br from-amber-400 to-yellow-500" 
            : "bg-gradient-to-br from-slate-600 to-slate-800"
        }`}
        style={{
          backgroundImage: result?.won && winResultImage 
            ? `url(${getResultImageUrl(winResultImage)})` 
            : !result?.won && loseImage 
            ? `url(${getResultImageUrl(loseImage)})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        {/* Dark overlay for readability when using background image */}
        {((result?.won && winResultImage) || (!result?.won && loseImage)) && (
          <div className="absolute inset-0 bg-black/40" />
        )}
        
        {/* Content */}
        <div className="relative z-10 text-center">
          {result?.won ? (
            <>
              <PartyPopper className={`text-white mb-3 mx-auto ${isFullScreen ? "w-16 h-16" : "w-12 h-12"}`} />
              <p className={`text-white font-bold text-center px-4 ${isFullScreen ? "text-2xl" : "text-xl"}`}>
                {result.prize?.name}
              </p>
              {result.prize?.description && isFullScreen && (
                <p className="text-white/80 text-center px-6 mt-2 text-sm">{result.prize.description}</p>
              )}
              {result.prize?.code && (
                <div className="mt-4 bg-white/20 rounded-lg px-4 py-2">
                  <p className="text-white/80 text-xs">Code</p>
                  <p className="text-white font-mono font-bold">{result.prize.code}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <Frown className={`text-white/50 mb-3 mx-auto ${isFullScreen ? "w-16 h-16" : "w-12 h-12"}`} />
              <p className={`text-white/80 text-center px-4 ${isFullScreen ? "text-xl" : "text-lg"}`}>
                {loseMessage}
              </p>
              <p className="text-white/50 text-sm mt-2">Réessayez plus tard</p>
            </>
          )}
        </div>
      </div>
      
      {/* Scratch layer */}
      {!revealed && (
        <canvas
          ref={canvasRef}
          width={cardWidth}
          height={cardHeight}
          className="absolute inset-0 w-full h-full cursor-pointer touch-none"
          onMouseDown={() => setIsScratching(true)}
          onMouseUp={() => setIsScratching(false)}
          onMouseLeave={() => setIsScratching(false)}
          onMouseMove={(e) => isScratching && handleScratch(e)}
          onTouchStart={() => setIsScratching(true)}
          onTouchEnd={() => setIsScratching(false)}
          onTouchMove={handleScratch}
        />
      )}
      
      {/* Scratch progress indicator */}
      {!revealed && scratchPercent > 0 && scratchPercent < 50 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full">
          {Math.round(scratchPercent)}% gratté
        </div>
      )}
    </div>
  );
};

export default function PublicGame() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(true); // Default to portrait fullscreen for scratch
  
  // Wallet state
  const [chanceConfig, setChanceConfig] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [purchaseAmount, setPurchaseAmount] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  
  const [formData, setFormData] = useState({
    player_phone: "",
    player_email: "",
    player_name: "",
    rgpd_consent: true
  });

  const wheelRef = useRef(null);

  useEffect(() => {
    loadGame();
    
    // Check for purchase confirmation in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get("purchase") === "success") {
      const phone = params.get("phone");
      const amount = params.get("amount");
      if (phone && amount) {
        confirmPurchase(phone, parseInt(amount));
      }
    }
  }, [id]);

  const loadGame = async () => {
    try {
      const [gameRes, configRes] = await Promise.all([
        publicGamesAPI.get(id),
        publicGamesAPI.getChanceConfig(id).catch(() => ({ data: null }))
      ]);
      setGame(gameRes.data);
      setChanceConfig(configRes.data);
      if (!gameRes.data.require_contact) {
        setShowForm(false);
      }
    } catch (error) {
      toast.error("Jeu non trouvé");
    } finally {
      setLoading(false);
    }
  };

  // Check wallet balance when phone changes
  useEffect(() => {
    if (formData.player_phone && formData.player_phone.length >= 10) {
      checkWalletBalance(formData.player_phone);
    }
  }, [formData.player_phone]);

  const checkWalletBalance = async (phone) => {
    try {
      const res = await publicGamesAPI.getBalance(phone);
      setWalletBalance(res.data.balance);
    } catch (error) {
      setWalletBalance(0);
    }
  };

  const confirmPurchase = async (phone, amount) => {
    try {
      await publicGamesAPI.confirmPurchase(id, phone, amount);
      toast.success(`${amount} chance(s) créditée(s) !`);
      setFormData(prev => ({ ...prev, player_phone: phone }));
      await checkWalletBalance(phone);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      // Already credited via webhook
    }
  };

  // Poll for payment status after Stripe redirect
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Délai de vérification dépassé. Vérifiez votre email pour la confirmation.");
      setPurchasing(false);
      return;
    }

    try {
      const res = await api.payments.getCheckoutStatus(sessionId);
      
      if (res.data.payment_status === "paid") {
        toast.success(`${res.data.quantity} chance(s) ajoutée(s) à votre compte !`);
        await checkWalletBalance(formData.player_phone);
        setPurchasing(false);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (res.data.status === "expired") {
        toast.error("Session de paiement expirée. Veuillez réessayer.");
        setPurchasing(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Payment status check error:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPurchasing(false);
      }
    }
  };

  // Check for payment return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentSuccess = params.get("payment_success");
    const paymentCancelled = params.get("payment_cancelled");

    if (sessionId && paymentSuccess) {
      setPurchasing(true);
      toast.info("Vérification du paiement en cours...");
      pollPaymentStatus(sessionId);
    } else if (paymentCancelled) {
      toast.info("Paiement annulé");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handlePurchaseChances = async () => {
    if (!formData.player_phone || formData.player_phone.length < 10) {
      toast.error("Veuillez d'abord entrer votre numéro de téléphone (10 chiffres)");
      return;
    }
    
    setPurchasing(true);
    try {
      const res = await api.payments.purchaseChances(id, formData.player_phone, purchaseAmount);
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'achat");
      setPurchasing(false);
    }
  };

  const fireConfetti = useCallback(() => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: [game?.primary_color || '#F59E0B', '#3B82F6', '#10B981']
    });
  }, [game]);

  const handlePlay = async () => {
    if (game?.require_contact && !formData.player_phone && !formData.player_email) {
      toast.error("Veuillez entrer votre téléphone ou email");
      return;
    }
    
    // Validate phone number length if provided
    if (formData.player_phone && formData.player_phone.length < 10) {
      toast.error("Le numéro de téléphone doit contenir au moins 10 chiffres");
      return;
    }
    
    setPlaying(true);
    setShowForm(false);
    
    try {
      const res = await publicGamesAPI.play(id, formData);
      setResult(res.data);
      
      if (game?.game_type === "wheel") {
        // Find winning prize index for wheel
        const winningIndex = res.data.won 
          ? game.prizes.findIndex(p => p.name === res.data.prize?.name)
          : Math.floor(Math.random() * game.prizes.length);
        
        wheelRef.current?.spin(winningIndex >= 0 ? winningIndex : 0);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du jeu");
      setPlaying(false);
      setShowForm(true);
    }
  };

  const handleWheelEnd = () => {
    setRevealed(true);
    if (result?.won) {
      setTimeout(fireConfetti, 300);
    }
  };

  const handleScratchReveal = () => {
    setRevealed(true);
    if (result?.won) {
      fireConfetti();
    }
  };

  const { canvasRef, spin } = FortuneWheel({
    prizes: game?.prizes || [],
    primaryColor: game?.primary_color || "#F59E0B",
    onSpinEnd: handleWheelEnd,
    spinning,
    setSpinning
  });
  
  wheelRef.current = { spin };

  // Extract YouTube video ID
  const extractYoutubeId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url?.match(regex);
    return match ? match[1] : null;
  };

  const getBackgroundStyle = () => {
    if (!game) return { background: "#1E293B" };
    
    const bgType = game.background_type || "color";
    
    if (bgType === "color") {
      return { background: game.background_color || "#1E293B" };
    }
    
    if (bgType === "image" && game.background_image) {
      const imageUrl = game.background_image.startsWith('/uploads') 
        ? `${process.env.REACT_APP_BACKEND_URL}${game.background_image}`
        : game.background_image;
      return {
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    }
    
    return { background: game.background_color || "#1E293B" };
  };

  const youtubeVideoId = game?.background_type === "video" && game?.background_video_url 
    ? extractYoutubeId(game.background_video_url) 
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!game || game.status !== "active") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <Card className="bg-white/10 border-white/20 max-w-md">
          <CardContent className="p-8 text-center">
            <Frown className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Jeu non disponible</h1>
            <p className="text-white/60">Ce jeu n'est pas actif ou n'existe pas.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={getBackgroundStyle()}
    >
      {/* YouTube Video Background */}
      {youtubeVideoId && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&loop=1&playlist=${youtubeVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 w-[200vw] h-[200vh] -translate-x-1/2 -translate-y-1/2"
            style={{ minWidth: "100%", minHeight: "100%" }}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Header with back button */}
        <header className="p-4 sm:p-6">
          {/* Back button and controls */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            
            {/* Wallet balance display */}
            <div className="flex items-center gap-2">
              {game.game_type === "scratch" && formData.player_phone?.length >= 10 && chanceConfig && (
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20">
                  <Ticket className="w-4 h-4 text-amber-400" />
                  <span className="text-white font-medium text-sm">
                    {walletBalance} chance{walletBalance !== 1 ? 's' : ''}
                  </span>
                  {chanceConfig?.allow_purchase && chanceConfig?.price_per_chance > 0 && (
                    <span className="text-white/50 text-xs">
                      ({(walletBalance * chanceConfig.price_per_chance).toFixed(0)}€)
                    </span>
                  )}
                </div>
              )}
              
              {/* Fullscreen toggle for scratch games */}
              {game.game_type === "scratch" && playing && !result && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  {isFullScreen ? (
                    <>
                      <Minimize2 className="w-4 h-4 mr-2" />
                      Paysage
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-4 h-4 mr-2" />
                      Portrait
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          {/* Logo and title */}
          <div className="text-center">
            {game.organizer_logo && (
              <img src={game.organizer_logo} alt="" className="h-12 mx-auto mb-4 rounded-lg" />
            )}
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              {game.title}
            </h1>
            {game.description && (
              <p className="text-white/70 max-w-md mx-auto text-sm sm:text-base">{game.description}</p>
            )}
            <p className="text-white/50 text-sm mt-2">{game.organizer_name}</p>
          </div>
        </header>

      {/* Main Game Area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
        <AnimatePresence mode="wait">
          {showForm && !playing ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-sm"
            >
              <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white text-center mb-6" style={{ fontFamily: 'Outfit' }}>
                    Participez au jeu !
                  </h3>
                  
                  <div className="space-y-4">
                    {game.require_contact && (
                      <>
                        <div className="relative">
                          <Phone className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                          <Input
                            type="tel"
                            placeholder="Votre téléphone (10 chiffres)"
                            value={formData.player_phone}
                            onChange={(e) => {
                              // Only allow digits and limit to 15 characters
                              const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                              setFormData({...formData, player_phone: value});
                            }}
                            minLength={10}
                            maxLength={15}
                            className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10"
                            data-testid="game-phone-input"
                          />
                          {formData.player_phone && formData.player_phone.length < 10 && (
                            <p className="text-xs text-amber-400 mt-1">Minimum 10 chiffres requis</p>
                          )}
                        </div>
                        
                        <div className="relative">
                          <Mail className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                          <Input
                            type="email"
                            placeholder="Votre email"
                            value={formData.player_email}
                            onChange={(e) => setFormData({...formData, player_email: e.target.value})}
                            className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10"
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="relative">
                      <User className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                      <Input
                        placeholder="Votre prénom (optionnel)"
                        value={formData.player_name}
                        onChange={(e) => setFormData({...formData, player_name: e.target.value})}
                        className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10"
                      />
                    </div>

                    {/* Wallet balance and purchase section for scratch games */}
                    {game.game_type === "scratch" && chanceConfig?.allow_purchase && formData.player_phone.length >= 10 && (
                      <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/70 text-sm">Vos chances disponibles</span>
                          <Badge className="bg-primary/20 text-primary border-0">
                            {walletBalance} chance{walletBalance !== 1 ? "s" : ""}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={chanceConfig.min_purchase}
                            max={chanceConfig.max_purchase}
                            value={purchaseAmount}
                            onChange={(e) => setPurchaseAmount(parseInt(e.target.value) || 1)}
                            className="w-20 h-9 bg-white/5 border-white/10 text-white text-center"
                          />
                          <Button
                            onClick={handlePurchaseChances}
                            disabled={purchasing || formData.player_phone.length < 10}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-white/20 text-white hover:bg-white/10"
                          >
                            {purchasing ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2" />
                            )}
                            Acheter ({(chanceConfig.price_per_chance * purchaseAmount).toFixed(2)}€)
                          </Button>
                        </div>
                        <p className="text-white/40 text-xs text-center">
                          {chanceConfig.price_per_chance.toFixed(2)}€ par chance
                        </p>
                      </div>
                    )}
                    
                    <Button
                      onClick={handlePlay}
                      className="w-full h-12 rounded-full text-white font-semibold"
                      style={{ background: game.primary_color }}
                      data-testid="play-game-btn"
                    >
                      {game.game_type === "wheel" ? "Tourner la roue !" : "Gratter ma carte !"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : game.game_type === "wheel" ? (
            <motion.div
              key="wheel"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Wheel */}
              <div className="relative mb-8">
                {/* Pointer */}
                <div 
                  className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10 w-0 h-0"
                  style={{
                    borderLeft: "15px solid transparent",
                    borderRight: "15px solid transparent",
                    borderTop: `25px solid ${game.primary_color}`,
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                  }}
                />
                <canvas
                  ref={canvasRef}
                  width={320}
                  height={320}
                  className="mx-auto"
                  style={{ filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))" }}
                />
              </div>

              {/* Result */}
              <AnimatePresence>
                {revealed && result && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className={`max-w-sm mx-auto ${
                      result.won 
                        ? "bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border-amber-500/30"
                        : "bg-white/10 border-white/20"
                    }`}>
                      <CardContent className="p-6 text-center">
                        {result.won ? (
                          <>
                            <PartyPopper className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                            <h3 className="text-2xl font-bold text-white mb-2">Félicitations !</h3>
                            <p className="text-white/80 mb-3">Vous avez gagné :</p>
                            <Badge className="text-lg px-4 py-2 bg-amber-500 text-white">
                              {result.prize?.name}
                            </Badge>
                            {result.prize?.code && (
                              <div className="mt-4 p-3 bg-white/10 rounded-lg">
                                <p className="text-white/60 text-sm">Votre code</p>
                                <p className="text-white font-mono text-xl">{result.prize.code}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <Frown className="w-12 h-12 mx-auto mb-3 text-white/50" />
                            <h3 className="text-xl font-bold text-white mb-2">Pas de chance !</h3>
                            <p className="text-white/60">Réessayez plus tard</p>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {!revealed && !spinning && playing && (
                <Button
                  onClick={handlePlay}
                  className="rounded-full text-white font-semibold px-8"
                  style={{ background: game.primary_color }}
                  disabled={spinning}
                >
                  <RotateCw className="w-5 h-5 mr-2" />
                  Tourner !
                </Button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="scratch"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <ScratchCard
                onReveal={handleScratchReveal}
                revealed={revealed}
                result={result}
                primaryColor={game.primary_color}
                scratchLogo={game.scratch_card_logo}
                scratchImage={game.scratch_card_image}
                isFullScreen={isFullScreen}
                loseMessages={game.lose_messages || [
                  "Pas de chance !",
                  "Dommage.. c'était pas loin !",
                  "Retentez votre chance !",
                  "Presque... Réessayez !"
                ]}
                loseImage={result?.lose_image || game.lose_image}
                loseVideoUrl={result?.lose_video_url || game.lose_video_url}
              />
              
              {!revealed && (
                <div className="mt-4 space-y-2">
                  <p className="text-white/60">Grattez au moins 50% de la carte pour découvrir votre gain !</p>
                  <p className="text-white/40 text-sm">
                    {isFullScreen ? "Mode portrait" : "Mode paysage"} - Cliquez sur le bouton en haut pour changer
                  </p>
                </div>
              )}

              {revealed && result?.won && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 space-y-4"
                >
                  <div className="p-4 bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl max-w-xs mx-auto border border-amber-500/30">
                    <PartyPopper className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                    <p className="text-white font-bold text-lg mb-1">Félicitations !</p>
                    {result.prize?.code && (
                      <>
                        <p className="text-white/60 text-sm">Votre code promo</p>
                        <p className="text-white font-mono text-2xl">{result.prize.code}</p>
                      </>
                    )}
                    {result.prize?.pickup_instructions && (
                      <p className="text-white/70 text-sm mt-2">{result.prize.pickup_instructions}</p>
                    )}
                  </div>

                  {/* Action buttons after winning */}
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    {walletBalance > 0 && (
                      <Button
                        onClick={() => {
                          setRevealed(false);
                          setResult(null);
                          setShowForm(true);
                        }}
                        className="w-full rounded-full text-white"
                        style={{ background: game.primary_color }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retenter ma chance ({walletBalance} restante{walletBalance > 1 ? "s" : ""})
                      </Button>
                    )}
                    {chanceConfig?.allow_purchase && walletBalance === 0 && (
                      <Button
                        onClick={() => {
                          setRevealed(false);
                          setResult(null);
                          setShowForm(true);
                        }}
                        className="w-full rounded-full text-white"
                        style={{ background: game.primary_color }}
                      >
                        <Coins className="w-4 h-4 mr-2" />
                        Acheter des chances
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => navigate("/")}
                      className="w-full rounded-full text-white border-white/30 hover:bg-white/10"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour à l'accueil
                    </Button>
                  </div>
                </motion.div>
              )}

              {revealed && !result?.won && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 space-y-4"
                >
                  {/* Action buttons after losing */}
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    {walletBalance > 0 ? (
                      <Button
                        onClick={() => {
                          setRevealed(false);
                          setResult(null);
                          setShowForm(true);
                        }}
                        className="w-full rounded-full text-white"
                        style={{ background: game.primary_color }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retenter ma chance ({walletBalance} restante{walletBalance > 1 ? "s" : ""})
                      </Button>
                    ) : chanceConfig?.allow_purchase ? (
                      <Button
                        onClick={() => {
                          setRevealed(false);
                          setResult(null);
                          setShowForm(true);
                        }}
                        className="w-full rounded-full text-white"
                        style={{ background: game.primary_color }}
                      >
                        <Coins className="w-4 h-4 mr-2" />
                        Acheter des chances ({chanceConfig.price_per_chance.toFixed(2)}€/chance)
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setRevealed(false);
                          setResult(null);
                          setPlaying(false);
                          setShowForm(true);
                        }}
                        className="w-full rounded-full text-white border-white/30 hover:bg-white/10"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Réessayer
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => navigate("/")}
                      className="w-full rounded-full text-white border-white/30 hover:bg-white/10"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour à l'accueil
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        {game.terms_conditions && (
          <p className="text-white/40 text-xs max-w-md mx-auto mb-4">
            {game.terms_conditions}
          </p>
        )}
        <p className="text-white/30 text-xs">
          Jeu organisé par {game.organizer_name}
        </p>
      </footer>
      </div>
    </div>
  );
}
